import React, { useMemo } from 'react';

import type { DayClassification } from '@src/domains/scheduling';

import { CellPriceText, CellRoot, StyledCalendarCell } from './styles';
import type { CellProps, CellVariant } from './types';

export const variantFor = (
  classification: DayClassification,
  outsideCurrentMonth: boolean,
  selected: boolean,
): CellVariant => {
  if (selected) return 'selected';
  if (outsideCurrentMonth) return 'disabled';
  if (classification.isAvailable) return 'available';
  if (classification.isSoldOut) return 'soldOut';
  return 'disabled';
};

const MAX_PRICE_FOR_FULL_DISPLAY = 1000;

const uniColorFor = (variant: CellVariant): 'success' | 'neutral' | undefined => {
  if (variant === 'available') return 'success';
  if (variant === 'soldOut') return 'neutral';
  return undefined;
};

const Cell = (props: CellProps) => {
  const { cellProps, classification, onSelect, pending = false } = props;
  const { outsideCurrentMonth = false, selected = false } = cellProps;
  const variant = variantFor(classification, outsideCurrentMonth, selected);

  const isUnselectable = pending || !classification.isAvailable || outsideCurrentMonth;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isUnselectable) return;
    onSelect();
    event.stopPropagation();
  };

  const dataCy = classification.isAvailable && !outsideCurrentMonth ? 'available_scheduling' : 'disabled_scheduling';

  const formattedPrice = useMemo(() => {
    let price = null;

    if (classification.price) {
      const priceNumber = Number(classification.price.fullPriceStr.replace(',', '.'));

      if (priceNumber >= MAX_PRICE_FOR_FULL_DISPLAY) {
        price = classification.price.priceStr;
      } else {
        price = classification.price.fullPriceStr;
      }
    }

    return price;
  }, [classification.price]);

  // Always render the price slot (even empty) while not pending. This keeps Uni's
  // bottomContent wrapper present so the day-number/today-dot/price positions stay
  // anchored at the same coordinates regardless of whether the cell actually has a
  // price — matching the Figma's fixed positions for every state.
  const bottomContent = pending ? null : <CellPriceText>{formattedPrice}</CellPriceText>;

  return (
    <CellRoot data-testid='day'>
      <StyledCalendarCell
        {...cellProps}
        selected={selected}
        pending={pending}
        disabled={isUnselectable}
        color={uniColorFor(variant)}
        onClick={handleClick}
        aria-label='day-button'
        aria-disabled={isUnselectable}
        data-cy={dataCy}
        disableRipple
        disableMargin
        bottomContent={bottomContent}
      />
    </CellRoot>
  );
};

export default Cell;
